import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { automunityAPI } from '../lib/api';
import toast, { Toaster } from 'react-hot-toast';
import Changelog from './Changelog';
import './AutomationView.css';

interface Automation {
  automation_id: string;
  name: string;
  description: string;
  team_name: string;
  team_names?: string;
  adl_user_id?: number;
  adl_name?: string;
  stage: string;
  identifier?: string;
  valuation_category?: string;
  cost_per_invocation?: string;
  minute_gain_per_objective?: string;
  hourly_rate_gain_per_objective?: string;
  development_hours?: string;
  resale_price?: number;
  resale_metric?: string;
  resale_cost?: number;
  stages?: Array<{ stage_id: number; name: string; description: string }>;
  contributors?: Array<{ contribution_id?: number; user_id?: number; full_name: string; role_name: string; contribution_description: string; [key: string]: any }>;
  services?: Array<{ automation_service_id?: number; service_id?: number; name: string }>;
  invocations?: Array<{
    invocation_id: string;
    duration: number;
    result_code: number;
    start_time: string;
    end_time: string;
  }>;
  time_saved?: number;
  money_saved?: number;
  application_url?: string;
  documentation_text?: string;
  documentation_diagram_url?: string;
}

interface Request {
  request_id: number;
  automation_id: number;
  type: string;
  priority: string;
  status: string;
  title: string;
  description: string;
  steps_to_reproduce: string;
  expected_behavior: string;
  submitted_by: number;
  submitted_by_name: string;
  assigned_to: number;
  assigned_to_name: string;
  created_at: string;
  updated_at: string;
}

const AutomationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [automation, setAutomation] = useState<Automation | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [teams, setTeams] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [hosts, setHosts] = useState<any[]>([]);
  const [allStages, setAllStages] = useState<any[]>([]);
  const [showContributorModal, setShowContributorModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{type: 'contributor' | 'service', id: number, name: string} | null>(null);
  const [showDiagram, setShowDiagram] = useState(false);
  const [requests, setRequests] = useState<Request[]>([]);
  const [requestSearch, setRequestSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [invocations, setInvocations] = useState<any[]>([]);
  const [invocationMetrics, setInvocationMetrics] = useState<any>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [newRequest, setNewRequest] = useState({ type: 'feature', priority: 'medium', title: '', description: '', steps_to_reproduce: '', expected_behavior: '' });
  const [linkedFunctions, setLinkedFunctions] = useState<any[]>([]);
  const [automationContributors, setAutomationContributors] = useState<any[]>([]);
  const [automationServices, setAutomationServices] = useState<any[]>([]);
  const [automationStages, setAutomationStages] = useState<any[]>([]);
  const [valueMetrics, setValueMetrics] = useState<any>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [automationWatchers, setAutomationWatchers] = useState<{ watcher_id: number; user_id: number; user_name: string; user_email: string; watch_preferences?: any }[]>([]);
  const [addingWatcher, setAddingWatcher] = useState(false);
  const [showWatchPreferences, setShowWatchPreferences] = useState(false);
  const [watchPreferences, setWatchPreferences] = useState<{ request_types: string[] }>({ request_types: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamsRes, usersRes, rolesRes, servicesRes, hostsRes, stagesRes] = await Promise.all([
          automunityAPI.getTeams(),
          automunityAPI.getUsers(),
          automunityAPI.getRoles(),
          automunityAPI.getServices(),
          automunityAPI.getHosts(),
          automunityAPI.getStages()
        ]);
        const [teamsData, usersData, rolesData, servicesData, hostsData, stagesData] = await Promise.all([
          teamsRes.body.json(),
          usersRes.body.json(),
          rolesRes.body.json(),
          servicesRes.body.json(),
          hostsRes.body.json(),
          stagesRes.body.json()
        ]);
        
        const teamsRecords = (teamsData as any)?.records || (Array.isArray(teamsData) ? teamsData : []);
        const usersRecords = (usersData as any)?.records || (Array.isArray(usersData) ? usersData : []);
        const rolesRecords = (rolesData as any)?.records || (Array.isArray(rolesData) ? rolesData : []);
        const servicesRecords = (servicesData as any)?.records || (Array.isArray(servicesData) ? servicesData : []);
        const hostsRecords = (hostsData as any)?.records || (Array.isArray(hostsData) ? hostsData : []);
        const stagesRecords = (stagesData as any)?.records || (Array.isArray(stagesData) ? stagesData : []);
        
        setTeams(Array.isArray(teamsRecords) ? teamsRecords : []);
        setUsers(Array.isArray(usersRecords) ? usersRecords : []);
        setRoles(Array.isArray(rolesRecords) ? rolesRecords : []);
        setServices(Array.isArray(servicesRecords) ? servicesRecords : []);
        setHosts(Array.isArray(hostsRecords) ? hostsRecords : []);
        setAllStages(Array.isArray(stagesRecords) ? stagesRecords : []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  // Resolve current user from Cognito session
  useEffect(() => {
    const resolveCurrentUser = async () => {
      try {
        const session = await fetchAuthSession();
        const email = (session.tokens?.idToken?.payload?.email as string || '').toLowerCase();
        if (email && users.length > 0) {
          const match = users.find((u: any) => u.email?.toLowerCase() === email);
          if (match) setCurrentUserId(match.user_id);
        }
      } catch { /* no-op */ }
    };
    resolveCurrentUser();
  }, [users]);

  useEffect(() => {
    const fetchAutomation = async () => {
      try {
        setLoading(true);
        const response = await automunityAPI.getAutomation(id || '1');
        const result = await response.body.json();
        
        if (!result) {
          setAutomation(null);
          return;
        }
        
        const automationData = typeof result === 'string' ? JSON.parse(result) : result;
        
        if (automationData) {
          // Parse JSON fields with null checks
          const parsed = {
            ...automationData,
            contributors: (() => {
              try {
                const val = automationData.contributors;
                if (!val) return [];
                return typeof val === 'string' ? JSON.parse(val) : val;
              } catch { return []; }
            })(),
            services: (() => {
              try {
                const val = automationData.services;
                if (!val) return [];
                return typeof val === 'string' ? JSON.parse(val) : val;
              } catch { return []; }
            })(),
            stages: (() => {
              try {
                const val = automationData.stages;
                if (!val) return [];
                return typeof val === 'string' ? JSON.parse(val) : val;
              } catch { return []; }
            })(),
            invocations: automationData.invocations || [],
          };
          setAutomation(parsed);
          setFormData({ ...parsed, show_resale: !!(parsed.resale_price || parsed.resale_metric || parsed.resale_cost) });
          
          // Always fetch native requests
          fetchRequests(id || '1');
          fetchInvocations(id || '1');
          fetchLinkedFunctions(id || '1');
          fetchAutomationContributors(id || '1');
          fetchAutomationWatchers(id || '1');
          fetchAutomationServices(id || '1');
          fetchAutomationStages(id || '1');
          fetchValueMetrics(id || '1');
        }
      } catch (err) {
        console.error('Error fetching automation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAutomation();
  }, [id]);

  const fetchRequests = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationRequests(automationId);
      const result = await response.body.json();
      const requestsData = (result as any)?.records || (Array.isArray(result) ? result : []);
      setRequests(requestsData);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setRequests([]);
    }
  };

  const fetchInvocations = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationInvocations(automationId);
      const result = await response.body.json();
      const invocationsData = (result as any)?.records || (Array.isArray(result) ? result : []);
      const metrics = (result as any)?.metrics || null;
      setInvocations(invocationsData);
      setInvocationMetrics(metrics);
    } catch (err) {
      console.error('Error fetching invocations:', err);
      setInvocations([]);
      setInvocationMetrics(null);
    }
  };

  const fetchLinkedFunctions = async (automationId: string) => {
    try {
      const response = await automunityAPI.getIntegrationConfigs(automationId);
      const result = await response.body.json();
      const data = (result as any)?.records || (Array.isArray(result) ? result : []);
      setLinkedFunctions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching linked functions:', err);
      setLinkedFunctions([]);
    }
  };

  const fetchAutomationContributors = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationContributions(automationId);
      const result = await response.body.json();
      const data = (result as any)?.records || (Array.isArray(result) ? result : []);
      setAutomationContributors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching contributors:', err);
      setAutomationContributors([]);
    }
  };

  const fetchAutomationWatchers = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationWatchers(automationId);
      const result = await response.body.json() as any;
      let data: any[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setAutomationWatchers(data);
    } catch (err) {
      console.error('Error fetching automation watchers:', err);
      setAutomationWatchers([]);
    }
  };

  const fetchAutomationServices = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationServices(automationId);
      const result = await response.body.json();
      const data = (result as any)?.records || (Array.isArray(result) ? result : []);
      // Filter active only and enrich with service name from loaded services list
      const active = (Array.isArray(data) ? data : []).filter((s: any) => s.is_active === 1 || s.is_active === '1');
      setAutomationServices(active);
    } catch (err) {
      console.error('Error fetching services:', err);
      setAutomationServices([]);
    }
  };

  const fetchAutomationStages = async (automationId: string) => {
    try {
      const response = await automunityAPI.getAutomationStages(automationId);
      const result = await response.body.json();
      const data = (result as any)?.records || (Array.isArray(result) ? result : []);
      // Sort by automation_stage_id (chronological insertion order)
      const sorted = (Array.isArray(data) ? data : [])
        .filter((s: any) => s.is_active === 1 || s.is_active === '1')
        .sort((a: any, b: any) => (a.automation_stage_id || 0) - (b.automation_stage_id || 0));
      setAutomationStages(sorted);
    } catch (err) {
      console.error('Error fetching stages:', err);
      setAutomationStages([]);
    }
  };

  const fetchValueMetrics = async (automationId: string) => {
    try {
      const response = await automunityAPI.getMonitorValue(automationId);
      const result = await response.body.json();
      const data = typeof (result as any)?.records === 'string' ? JSON.parse((result as any).records) : (result as any)?.records || result;
      // data has { automations: [...], totals: {...} }
      const automations = data?.automations || [];
      if (automations.length > 0) {
        setValueMetrics(automations[0]);
      }
    } catch (err) {
      console.error('Error fetching value metrics:', err);
    }
  };

  const handleSave = async () => {
    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        adl_user_id: formData.adl_user_id || null,
        team_id: formData.team_id,
        host_id: formData.host_id,
        date_created: formData.date_created,
        application_url: formData.application_url,
        documentation_diagram_url: formData.documentation_diagram_url,
        documentation_text: formData.documentation_text
      };
      await automunityAPI.updateAutomation(id!, updateData);

      // Save stage if changed
      const currentStageId = automationStages.length > 0 ? automationStages[automationStages.length - 1]?.stage_id : null;
      if (formData.pendingStageId && formData.pendingStageId !== currentStageId) {
        await automunityAPI.addAutomationStage(id!, { stage_id: String(formData.pendingStageId) });
      }
      
      setEditMode(false);
      toast.success('Automation updated successfully');
      window.location.reload();
    } catch (err: any) {
      toast.error(`Failed to update: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleFinanceSave = async () => {
    try {
      const updateData = {
        identifier: formData.identifier,
        valuation_category: formData.valuation_category,
        cost_per_invocation: formData.cost_per_invocation,
        minute_gain_per_objective: formData.minute_gain_per_objective,
        hourly_rate_gain_per_objective: formData.hourly_rate_gain_per_objective,
        development_hours: formData.development_hours,
        monthly_value_saved: formData.monthly_value_saved || null,
        adl_user_id: formData.adl_user_id || null,
        resale_price: formData.resale_price || null,
        resale_metric: formData.resale_metric || null,
        resale_cost: formData.resale_cost || null
      };
      await automunityAPI.updateAutomation(id!, updateData);
      toast.success('Financial data updated successfully');
      window.location.reload();
    } catch (err) {
      console.error('Error updating financial data:', err);
      toast.error('Failed to update financial data');
    }
  };



  const handleAddContributor = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const roleId = (form.elements.namedItem('role_id') as HTMLSelectElement).value;
    const customDesc = (form.elements.namedItem('description') as HTMLTextAreaElement).value;
    const role = roles.find((r: any) => r.role_id === Number(roleId));
    const data = {
      user_id: (form.elements.namedItem('user_id') as HTMLSelectElement).value,
      role_id: roleId,
      description: customDesc || role?.description || role?.name || 'Contributor'
    };
    try {
      await automunityAPI.createContribution(id!, data);
      toast.success('Contributor added');
      setShowContributorModal(false);
      window.location.reload();
    } catch (err: any) {
      toast.error(`Failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleAddService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = {
      service_id: (form.elements.namedItem('service_id') as HTMLSelectElement).value
    };
    try {
      await automunityAPI.addAutomationService(id!, data);
      toast.success('Service added');
      setShowServiceModal(false);
      window.location.reload();
    } catch (err: any) {
      toast.error(`Failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteContributor = async (contributionId: number) => {
    try {
      await automunityAPI.deleteContribution(id!, contributionId);
      toast.success('Contributor removed successfully');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      window.location.reload();
    } catch (err) {
      console.error('Error deleting contributor:', err);
      toast.error('Failed to remove contributor');
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    try {
      // For automation_service table, we need to use service_id since there's no automation_service_id
      // The Lambda will use automation_id from the URL path
      await automunityAPI.deleteAutomationService(id!, serviceId);
      toast.success('Service removed successfully');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      window.location.reload();
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error('Failed to remove service');
    }
  };

  if (loading) return <div className="loading">Loading automation details...</div>;
  if (!automation) return <div className="error">Automation not found</div>;

  const successfulInvocations = invocationMetrics?.successful_invocations || 0;
  const totalInvocations = invocationMetrics?.total_invocations || 0;
  const successRate = invocationMetrics?.success_rate || 0;
  const latestStage = automationStages.length > 0 
    ? allStages.find((s: any) => s.stage_id === automationStages[automationStages.length - 1]?.stage_id)?.name || 'Unknown'
    : 'Not Set';

  return (
    <>
      <Toaster position="top-right" />
      <div className="automation-view">
      <div className="automation-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>{automation.name}</h1>
          <button onClick={() => setEditMode(!editMode)} className="btn btn-primary">
            {editMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <p>{automation.description}</p>
        <div className="automation-meta">
          {automation.adl_name && <span className="team">ADL: {automation.adl_name}</span>}
          <span className="team">Team: {automation.team_names || automation.team_name || 'Unassigned'}</span>
          <span className="team">Stage: {latestStage}</span>
        </div>
      </div>

      {editMode && (
        <div className="edit-form portlet">
          <div className="portlet-body">
            <h3>Edit Automation</h3>
            <form>
              <div className="form-group">
                <label>Name</label>
                <input type="text" className="form-control" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Developer Lead (ADL)</label>
                <select className="form-control" value={formData.adl_user_id || ''} onChange={(e) => setFormData({ ...formData, adl_user_id: e.target.value || null })}>
                  <option value="">No ADL assigned</option>
                  {users.filter((u: any) => u.is_developer).map((u: any) => (
                    <option key={u.user_id} value={u.user_id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Teams</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {(() => {
                    const teamIds = formData.team_id ? [Number(formData.team_id)] : [];
                    return teamIds.map(tid => {
                      const t = teams.find(team => team.team_id === tid);
                      return t ? (
                        <span key={tid} style={{ background: '#e3f2fd', color: '#1565c0', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {t.name}
                          <span onClick={() => setFormData({ ...formData, team_id: '' })} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                        </span>
                      ) : null;
                    });
                  })()}
                </div>
                <select className="form-control" value="" onChange={(e) => {
                  if (e.target.value) {
                    setFormData({ ...formData, team_id: e.target.value });
                    // Also add to junction table
                    automunityAPI.addAutomationTeam(id || '', Number(e.target.value), true);
                  }
                }}>
                  <option value="">+ Add Team...</option>
                  {teams.sort((a, b) => a.name.localeCompare(b.name)).map(team => <option key={team.team_id} value={team.team_id}>{team.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Hosting Platform</label>
                <select className="form-control" value={formData.host_id || ''} onChange={(e) => setFormData({ ...formData, host_id: e.target.value })}>
                  <option value="">Select a Host...</option>
                  {hosts.sort((a, b) => a.name.localeCompare(b.name)).map(host => <option key={host.host_id} value={host.host_id}>{host.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Date Created</label>
                <input type="date" className="form-control" value={formData.date_created || ''} onChange={(e) => setFormData({ ...formData, date_created: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Stage</label>
                <select 
                  className="form-control" 
                  onChange={(e) => setFormData({ ...formData, pendingStageId: Number(e.target.value) })}
                  value={formData.pendingStageId || (automationStages.length > 0 ? automationStages[automationStages.length - 1]?.stage_id : "")}
                >
                  <option value="">Select Stage...</option>
                  {allStages.sort((a, b) => a.name.localeCompare(b.name)).map(stage => (
                    <option key={stage.stage_id} value={stage.stage_id}>{stage.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Application URL</label>
                <input type="text" className="form-control" value={formData.application_url || ''} onChange={(e) => setFormData({ ...formData, application_url: e.target.value })} placeholder="Link to Application (if applicable)" />
              </div>
              <div className="form-group">
                <label>Diagram URL</label>
                <input type="text" className="form-control" value={formData.documentation_diagram_url || ''} onChange={(e) => setFormData({ ...formData, documentation_diagram_url: e.target.value })} placeholder="Link to LucidChart URL" />
              </div>
              <div className="form-group">
                <label>Documentation</label>
                <textarea className="form-control" value={formData.documentation_text || ''} onChange={(e) => setFormData({ ...formData, documentation_text: e.target.value })} placeholder="HTML Documentation for Developers" />
              </div>
              <button type="button" onClick={handleSave} className="btn btn-success">Save Changes</button>
            </form>
          </div>
        </div>
      )}

      <div className="stats-row">
        <div className="stat-tile">
          <h3>Contributors</h3>
          <div className="stat-value">{automationContributors.length || 0}</div>
        </div>
        <div className="stat-tile">
          <h3>Watchers</h3>
          <div className="stat-value">{automationWatchers.length || 0}</div>
        </div>
        <div className="stat-tile">
          <h3>Services</h3>
          <div className="stat-value">{automationServices.length || 0}</div>
        </div>
        <div className="stat-tile">
          <h3>Time Saved</h3>
          <div className="stat-value">{valueMetrics?.time_saved_hours?.toFixed(1) || '0'}h</div>
        </div>
        <div className="stat-tile">
          <h3>Money Saved</h3>
          <div className="stat-value">${valueMetrics?.money_saved?.toFixed(0) || '0'}</div>
        </div>
      </div>

      <div className="tabs">
        <div className="tab-nav">
          <button 
            className={activeTab === 'overview' ? 'active' : ''} 
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'contributors' ? 'active' : ''} 
            onClick={() => setActiveTab('contributors')}
          >
            Contributors
          </button>
          <button 
            className={activeTab === 'finance' ? 'active' : ''} 
            onClick={() => setActiveTab('finance')}
          >
            Finance
          </button>
          <button 
            className={activeTab === 'invocations' ? 'active' : ''} 
            onClick={() => setActiveTab('invocations')}
          >
            Invocations
          </button>
          <button 
            className={activeTab === 'requests' ? 'active' : ''} 
            onClick={() => setActiveTab('requests')}
          >
            Requests
          </button>
          <button 
            className={activeTab === 'documentation' ? 'active' : ''} 
            onClick={() => setActiveTab('documentation')}
          >
            Documentation
          </button>
          <button 
            className={activeTab === 'changelog' ? 'active' : ''} 
            onClick={() => setActiveTab('changelog')}
          >
            Changelog
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="row">
                <div className="col-md-6">
                  <div className="portlet">
                    <div className="portlet-body">
                      <h3>Quick Stats</h3>
                      <ul>
                        <li><strong>Total Invocations:</strong> {totalInvocations}</li>
                        <li><strong>Total Invocations:</strong> {valueMetrics?.total_invocations || totalInvocations || 0}</li>
                        <li><strong>Successful:</strong> {valueMetrics?.successful_invocations || 0}</li>
                        <li><strong>Errors:</strong> {valueMetrics?.total_errors || 0}</li>
                        <li><strong>Total Time Saved:</strong> {valueMetrics?.time_saved_hours?.toFixed(1) || '0'}h</li>
                        <li><strong>Total Money Saved:</strong> ${valueMetrics?.money_saved?.toFixed(2) || '0'}</li>
                      </ul>
                      {automation.application_url && (
                        <a href={automation.application_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                          View Application
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="portlet">
                    <div className="portlet-body">
                      <h3>Services Used</h3>
                      <button onClick={() => setShowServiceModal(true)} className="btn btn-primary" style={{ marginBottom: '10px' }}>Add Service</button>
                      {automationServices.length > 0 ? (
                        <ul>
                          {automationServices.map((service: any, index: number) => {
                            const serviceName = services.find((s: any) => s.service_id === service.service_id)?.name || `Service #${service.service_id}`;
                            return (
                            <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                              <span>{serviceName}</span>
                              <button 
                                onClick={() => {
                                  setDeleteTarget({ type: 'service', id: service.service_id, name: serviceName });
                                  setShowDeleteModal(true);
                                }}
                                className="btn btn-secondary" 
                                style={{ padding: '2px 8px', fontSize: '12px' }}
                              >
                                Delete
                              </button>
                            </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p>No services configured</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Automation Watchers Section */}
              <div className="row" style={{ marginTop: '16px' }}>
                <div className="col-md-12">
                  <div className="portlet">
                    <div className="portlet-body">
                      <h3>Watchers</h3>
                      <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                        Watchers receive email notifications when requests are created, status changes, comments are added, or assignments change under this automation.
                      </p>

                      {/* Current watchers list */}
                      {automationWatchers.length > 0 ? (
                        <div style={{ marginBottom: '12px' }}>
                          {automationWatchers.map((w: any) => (
                            <div key={w.watcher_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #eee' }}>
                              <span style={{ fontSize: '13px' }}>{w.user_name || w.user_email || `User #${w.user_id}`}</span>
                              <button
                                onClick={async () => {
                                  await automunityAPI.removeAutomationWatcher(id || '', w.user_id);
                                  fetchAutomationWatchers(id || '');
                                  toast.success('Watcher removed');
                                }}
                                style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}
                                title="Remove watcher"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No one is watching this automation yet.</p>
                      )}

                      {/* Watch/Unwatch button for current user */}
                      {currentUserId && !automationWatchers.find(w => w.user_id === currentUserId) ? (
                        <button
                          onClick={async () => {
                            if (showWatchPreferences) {
                              // Submit with preferences
                              await automunityAPI.addAutomationWatcher(id || '', currentUserId, watchPreferences.request_types.length > 0 ? watchPreferences : undefined);
                              setShowWatchPreferences(false);
                              setWatchPreferences({ request_types: [] });
                            } else {
                              await automunityAPI.addAutomationWatcher(id || '', currentUserId);
                            }
                            fetchAutomationWatchers(id || '');
                            toast.success('You are now watching this automation');
                          }}
                          style={{ width: '100%', padding: '8px', background: '#e8f4fd', border: '1px solid #b8daff', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#0056b3', marginBottom: '8px' }}
                        >
                          👁 Watch this automation
                        </button>
                      ) : currentUserId ? (
                        <button
                          onClick={async () => {
                            await automunityAPI.removeAutomationWatcher(id || '', currentUserId);
                            fetchAutomationWatchers(id || '');
                            toast.success('You are no longer watching this automation');
                          }}
                          style={{ width: '100%', padding: '8px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#856404', marginBottom: '8px' }}
                        >
                          🔕 Unwatch this automation
                        </button>
                      ) : null}

                      {/* Watch preferences toggle */}
                      {currentUserId && !automationWatchers.find(w => w.user_id === currentUserId) && (
                        <div style={{ marginBottom: '8px' }}>
                          <button
                            onClick={() => setShowWatchPreferences(!showWatchPreferences)}
                            style={{ background: 'none', border: 'none', fontSize: '12px', color: '#6c757d', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {showWatchPreferences ? 'Hide preferences' : 'Customize what you receive notifications for...'}
                          </button>
                          {showWatchPreferences && (
                            <div style={{ marginTop: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                              <p style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Notify me about these request types:</p>
                              {['bug', 'feature', 'improvement', 'task', 'documentation'].map(type => (
                                <label key={type} style={{ display: 'block', fontSize: '12px', marginBottom: '4px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={watchPreferences.request_types.includes(type)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setWatchPreferences(prev => ({ ...prev, request_types: [...prev.request_types, type] }));
                                      } else {
                                        setWatchPreferences(prev => ({ ...prev, request_types: prev.request_types.filter(t => t !== type) }));
                                      }
                                    }}
                                    style={{ marginRight: '6px' }}
                                  />
                                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                                </label>
                              ))}
                              <p style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>Leave all unchecked to receive notifications for all types.</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Admin: add other users as watchers */}
                      <div style={{ marginTop: '8px' }}>
                        {addingWatcher ? (
                          <select
                            onChange={async (e) => {
                              if (e.target.value) {
                                await automunityAPI.addAutomationWatcher(id || '', Number(e.target.value));
                                fetchAutomationWatchers(id || '');
                                setAddingWatcher(false);
                                toast.success('Watcher added');
                              }
                            }}
                            onBlur={() => setAddingWatcher(false)}
                            autoFocus
                            style={{ width: '100%', padding: '6px', fontSize: '13px' }}
                          >
                            <option value="">Select user to add as watcher...</option>
                            {users
                              .filter((u: any) => u.is_active && !automationWatchers.find(w => w.user_id === u.user_id))
                              .sort((a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || ''))
                              .map((u: any) => (
                                <option key={u.user_id} value={u.user_id}>{u.full_name}</option>
                              ))
                            }
                          </select>
                        ) : (
                          <button
                            onClick={() => setAddingWatcher(true)}
                            style={{ width: '100%', padding: '6px', background: '#f8f9fa', border: '1px dashed #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#666' }}
                          >
                            + Add watcher
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contributors' && (
            <div className="contributors-tab">
              <div className="portlet">
                <div className="portlet-body">
                  <div style={{ marginBottom: '15px' }}>
                    <button onClick={() => setShowContributorModal(true)} className="btn btn-primary">Add Contributor</button>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Contributor</th>
                        <th>Role</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {automationContributors.length > 0 ? (
                        automationContributors.map((contributor: any, index: number) => {
                          const userName = users.find((u: any) => u.user_id === contributor.user_id)?.full_name || contributor?.full_name || 'Unknown';
                          const roleName = roles.find((r: any) => r.role_id === contributor.role_id)?.name || contributor?.role_name || 'Unknown';
                          return (
                          <tr key={index}>
                            <td>{userName}</td>
                            <td>{roleName}</td>
                            <td>{contributor?.description || contributor?.contribution_description || 'N/A'}</td>
                            <td>
                              <button
                                onClick={() => {
                                  const contributorId = contributor.contribution_id;
                                  if (!contributorId) {
                                    toast.error('Contributor ID not found');
                                    return;
                                  }
                                  setDeleteTarget({ type: 'contributor', id: contributorId, name: userName });
                                  setShowDeleteModal(true);
                                }}
                                className="btn btn-secondary"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={4}>No contributors found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'finance' && (
            <div className="finance-tab">
              <div className="finance-split">
                <div className="finance-left">
                  <div className="portlet">
                    <div className="portlet-body">
                      <h3>Value Metrics</h3>
                      <form>
                        <div className="form-group">
                          <label>Identifier</label>
                          <input type="text" className="form-control" value={formData.identifier || ''} onChange={(e) => setFormData({ ...formData, identifier: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Valuation Category</label>
                          <select className="form-control" value={formData.valuation_category || ''} onChange={(e) => setFormData({ ...formData, valuation_category: e.target.value })}>
                            <option value="">Select a Category...</option>
                            <option value="1">Time Saved (per invocation)</option>
                            <option value="2">Risk Reduction (fixed monthly)</option>
                            <option value="3">Cost Optimization (fixed monthly)</option>
                            <option value="4">Enablement (fixed monthly)</option>
                            <option value="5">New Process (fixed monthly)</option>
                            <option value="6">Product (resale revenue)</option>
                            <option value="7">Value Add (client offering enhancement)</option>
                          </select>
                        </div>

                        {/* Per-invocation fields — Time Saved */}
                        {formData.valuation_category === '1' && (
                          <>
                            <div className="form-group">
                              <label>Time Saved per Invocation (mins)</label>
                              <input type="text" className="form-control" value={formData.minute_gain_per_objective || ''} onChange={(e) => setFormData({ ...formData, minute_gain_per_objective: e.target.value })} placeholder="Minutes saved each time this runs successfully" />
                            </div>
                            <div className="form-group">
                              <label>Hourly Rate ($)</label>
                              <input type="text" className="form-control" value={formData.hourly_rate_gain_per_objective || ''} onChange={(e) => setFormData({ ...formData, hourly_rate_gain_per_objective: e.target.value })} placeholder="Billing rate of the person whose time is saved" />
                            </div>
                            <div className="form-group">
                              <label>Cost per Invocation ($)</label>
                              <input type="text" className="form-control" value={formData.cost_per_invocation || ''} onChange={(e) => setFormData({ ...formData, cost_per_invocation: e.target.value })} placeholder="What it costs to run (Lambda cost, API calls, etc.)" />
                            </div>
                            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '-8px' }}>
                              Formula: (mins saved × invocations × rate/60) − (cost × invocations)
                            </p>
                          </>
                        )}

                        {/* Fixed recurring fields — Risk, Cost Opt, Enablement, New Process, Value Add */}
                        {['2', '3', '4', '5', '7'].includes(formData.valuation_category) && (
                          <>
                            <div className="form-group">
                              <label>Monthly Value Saved ($)</label>
                              <input type="number" step="0.01" className="form-control" value={formData.monthly_value_saved || ''} onChange={(e) => setFormData({ ...formData, monthly_value_saved: e.target.value })} placeholder="Estimated monthly dollar value this automation provides" />
                            </div>
                            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '-8px' }}>
                              {formData.valuation_category === '2' && 'What would the monthly cost of this risk be without this automation?'}
                              {formData.valuation_category === '3' && 'How much does this save per month (reduced licensing, avoided labor, etc.)?'}
                              {formData.valuation_category === '4' && 'What monthly value does this enable that wasn\'t possible before?'}
                              {formData.valuation_category === '5' && 'What would it cost monthly to do this process manually?'}
                              {formData.valuation_category === '7' && 'What is the monthly value of this enhancement provided free to clients on top of existing offerings?'}
                            </p>
                          </>
                        )}

                        {/* Resale fields — Product */}
                        {formData.valuation_category === '6' && (
                          <p style={{ fontSize: '12px', color: '#64748b' }}>
                            Configure resale pricing in the Resale section below.
                          </p>
                        )}

                        <div className="form-group">
                          <label>Development Hours</label>
                          <input type="number" className="form-control" value={formData.development_hours || ''} onChange={(e) => setFormData({ ...formData, development_hours: e.target.value })} placeholder="Total hours invested in building this" />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="finance-right">
                  <div className="portlet">
                    <div className="portlet-body">
                      <label className="resale-toggle">
                        <input type="checkbox" checked={!!formData.show_resale} onChange={(e) => setFormData({ ...formData, show_resale: e.target.checked })} />
                        Can be resold?
                      </label>
                      {formData.show_resale && (
                        <>
                          <h3>Resale Attributes</h3>
                          <form>
                            <div className="form-group">
                              <label>Pricing Metric</label>
                              <select className="form-control" value={formData.resale_metric || ''} onChange={(e) => setFormData({ ...formData, resale_metric: e.target.value })}>
                                <option value="">Select a Metric...</option>
                                <option value="per_user">Per User</option>
                                <option value="per_client">Per Client</option>
                                <option value="per_target">Per Target</option>
                                <option value="per_device">Per Device</option>
                                <option value="per_month">Per Month</option>
                                <option value="per_year">Per Year</option>
                                <option value="flat_rate">Flat Rate</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Cost (per chosen metric)</label>
                              <input type="number" step="0.01" min="0" className="form-control" value={formData.resale_cost || ''} onChange={(e) => setFormData({ ...formData, resale_cost: e.target.value })} placeholder="Cost per unit of the chosen metric" />
                            </div>
                            <div className="form-group">
                              <label>Resale Price ($)</label>
                              <input type="number" step="0.01" min="0" className="form-control" value={formData.resale_price || ''} onChange={(e) => setFormData({ ...formData, resale_price: e.target.value })} placeholder="Resale value for this automation" />
                            </div>
                          </form>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button type="button" onClick={handleFinanceSave} className="btn btn-success" style={{ marginTop: '15px' }}>Save Financial Data</button>
            </div>
          )}

          {activeTab === 'invocations' && (
            <div className="invocations-tab">
              <div className="portlet">
                <div className="portlet-body">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Duration (sec)</th>
                        <th>Result</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invocations && invocations.length > 0 ? (
                        invocations.map((invocation) => (
                          <tr key={invocation.invocation_id}>
                            <td>{invocation.invocation_id}</td>
                            <td>{(invocation.duration / 1000).toFixed(2)}</td>
                            <td>{invocation.result_code === 3 ? 'Success' : 'Failed'}</td>
                            <td>{invocation.start_time}</td>
                            <td>{invocation.end_time}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>No invocations found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="requests-tab">
              <div className="portlet">
                <div className="portlet-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Feature & Bug Requests</h3>
                    <button className="btn btn-primary" onClick={() => setShowRequestModal(true)}>
                      + New Request
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <select 
                      className="form-control" 
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ maxWidth: '150px' }}
                    >
                      <option value="">All Statuses</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="completed">Completed</option>
                      <option value="closed">Closed</option>
                    </select>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Search requests..." 
                      value={requestSearch}
                      onChange={(e) => setRequestSearch(e.target.value)}
                      style={{ maxWidth: '300px' }}
                    />
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Title</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Submitted By</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.filter(r => {
                        const matchesStatus = !statusFilter || r.status === statusFilter;
                        const matchesSearch = !requestSearch || 
                          r.title?.toLowerCase().includes(requestSearch.toLowerCase()) ||
                          r.description?.toLowerCase().includes(requestSearch.toLowerCase());
                        return matchesStatus && matchesSearch;
                      }).length > 0 ? (
                        requests.filter(r => {
                          const matchesStatus = !statusFilter || r.status === statusFilter;
                          const matchesSearch = !requestSearch || 
                            r.title?.toLowerCase().includes(requestSearch.toLowerCase()) ||
                            r.description?.toLowerCase().includes(requestSearch.toLowerCase());
                          return matchesStatus && matchesSearch;
                        }).map((request) => (
                          <tr key={request.request_id} onClick={() => window.open(`/requests/${request.request_id}`, '_blank')} style={{ cursor: 'pointer' }}>
                            <td>#{request.request_id}</td>
                            <td>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: request.type === 'bug' ? '#fce4e4' : request.type === 'feature' ? '#e4f4fc' : '#f0f0f0',
                                color: request.type === 'bug' ? '#c0392b' : request.type === 'feature' ? '#2980b9' : '#555'
                              }}>
                                {request.type}
                              </span>
                            </td>
                            <td>{request.title}</td>
                            <td>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: request.priority === 'critical' ? '#fce4e4' : request.priority === 'high' ? '#fff3e0' : '#f0f0f0',
                                color: request.priority === 'critical' ? '#c0392b' : request.priority === 'high' ? '#e67e22' : '#555'
                              }}>
                                {request.priority}
                              </span>
                            </td>
                            <td>{request.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                            <td>{request.submitted_by_name || 'Unknown'}</td>
                            <td>{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7}>No requests yet. Click "+ New Request" to submit one.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documentation' && (
            <div className="documentation-tab">
              <div className="portlet">
                <div className="portlet-body">
                  <div className="diagram-section">
                    {automation.documentation_diagram_url ? (
                      <>
                        <button 
                          onClick={() => setShowDiagram(!showDiagram)} 
                          className="btn btn-primary" 
                          style={{ marginBottom: '10px' }}
                        >
                          {showDiagram ? 'Hide Diagram' : 'Show Diagram'}
                        </button>
                        {showDiagram && (
                          <div style={{ width: '640px', height: '480px', margin: '10px', position: 'relative' }}>
                            <iframe 
                              allowFullScreen 
                              frameBorder="0" 
                              style={{ width: '640px', height: '480px' }} 
                              src={automation.documentation_diagram_url}
                              title="Automation Diagram"
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <p>No diagram available</p>
                    )}
                  </div>
                  {automation.documentation_text && (
                    <div className="text-section">
                      <h3>Documentation</h3>
                      <div dangerouslySetInnerHTML={{ __html: automation.documentation_text }} />
                    </div>
                  )}
                </div>
              </div>

              {/* AWS Resources Section */}
              <div className="portlet" style={{ marginTop: '20px' }}>
                <div className="portlet-body">
                  <h3>Resources</h3>
                  <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>Lambda functions linked to this automation via CloudFormation tags.</p>
                  {linkedFunctions.length > 0 ? (
                    <table className="resources-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #eee', fontSize: '12px', color: '#666' }}>Function Name</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #eee', fontSize: '12px', color: '#666' }}>Status</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #eee', fontSize: '12px', color: '#666' }}>Schedule</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #eee', fontSize: '12px', color: '#666' }}>Source Path</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkedFunctions.map((fn: any) => (
                          <tr key={fn.integration_config_id}>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace', fontSize: '13px' }}>{fn.function_identifier}</td>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                              <span style={{ 
                                padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, color: 'white',
                                background: fn.latest_status === 'healthy' ? '#28a745' : fn.latest_status === 'critical' ? '#dc3545' : fn.latest_status === 'warning' ? '#ffc107' : '#6c757d'
                              }}>
                                {fn.latest_status || 'unknown'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: '#666' }}>{fn.expected_schedule_description || '—'}</td>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', color: '#888', fontFamily: 'monospace' }}>{fn.source_path || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: '#999' }}>No Lambda functions linked to this automation.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'changelog' && (
            <div className="changelog-tab">
              <Changelog automationId={id} embedded={true} />
            </div>
          )}
        </div>
      </div>

      {showContributorModal && (
        <div className="modal-overlay" onClick={() => setShowContributorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Contributor</h3>
            <form onSubmit={handleAddContributor}>
              <div className="form-group">
                <label>User</label>
                <select name="user_id" className="form-control" required>
                  <option value="">Select User...</option>
                  {users.sort((a, b) => a.full_name.localeCompare(b.full_name)).map(user => <option key={user.user_id} value={user.user_id}>{user.full_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select name="role_id" className="form-control" required>
                  <option value="">Select Role...</option>
                  {roles.sort((a, b) => a.name.localeCompare(b.name)).map(role => <option key={role.role_id} value={role.role_id}>{role.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea name="description" className="form-control" placeholder="Describe the contribution" />
              </div>
              <button type="submit" className="btn btn-success">Add</button>
              <button type="button" onClick={() => setShowContributorModal(false)} className="btn btn-secondary">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showServiceModal && (
        <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Service</h3>
            <form onSubmit={handleAddService}>
              <div className="form-group">
                <label>Service</label>
                <select name="service_id" className="form-control" required key={services.length}>
                  <option value="">Select Service...</option>
                  {services.sort((a, b) => a.name.localeCompare(b.name)).map(service => <option key={service.service_id} value={service.service_id}>{service.name}</option>)}
                </select>
              </div>
              <button type="submit" className="btn btn-success">Add</button>
              <button type="button" onClick={() => setShowServiceModal(false)} className="btn btn-secondary">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <h3>New Request</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await automunityAPI.createRequest(id!, newRequest);
                toast.success('Request submitted');
                setShowRequestModal(false);
                setNewRequest({ type: 'feature', priority: 'medium', title: '', description: '', steps_to_reproduce: '', expected_behavior: '' });
                fetchRequests(id || '1');
              } catch (err) {
                console.error('Error creating request:', err);
                toast.error('Failed to submit request');
              }
            }}>
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" value={newRequest.type} onChange={(e) => setNewRequest({ ...newRequest, type: e.target.value })}>
                  <option value="feature">Feature Request</option>
                  <option value="bug">Bug Report</option>
                  <option value="improvement">Improvement</option>
                  <option value="question">Question</option>
                  <option value="administrative">Administrative</option>
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select className="form-control" value={newRequest.priority} onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value })}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="form-group">
                <label>Title</label>
                <input type="text" className="form-control" required value={newRequest.title} onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })} placeholder="Brief summary of the request" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" rows={3} value={newRequest.description} onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })} placeholder="Detailed description..." />
              </div>
              {newRequest.type === 'bug' && (
                <>
                  <div className="form-group">
                    <label>Steps to Reproduce</label>
                    <textarea className="form-control" rows={3} value={newRequest.steps_to_reproduce} onChange={(e) => setNewRequest({ ...newRequest, steps_to_reproduce: e.target.value })} placeholder="1. Do this&#10;2. Then this&#10;3. See error" />
                  </div>
                  <div className="form-group">
                    <label>Expected Behavior</label>
                    <textarea className="form-control" rows={2} value={newRequest.expected_behavior} onChange={(e) => setNewRequest({ ...newRequest, expected_behavior: e.target.value })} placeholder="What should happen instead?" />
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRequestModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-success">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button 
                className="close-button"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove <strong>{deleteTarget.name}</strong>?</p>
              <p style={{ color: '#666', fontSize: '14px' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => {
                  if (deleteTarget.type === 'contributor') {
                    handleDeleteContributor(deleteTarget.id);
                  } else {
                    handleDeleteService(deleteTarget.id);
                  }
                }}
                style={{ backgroundColor: '#dc3545' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AutomationView;